import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Form,
  Button,
  InputNumber,
  message,
  Select,
  Table,
  Switch,
  Modal,
  Tag,
  Radio,
} from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import CurrencyConversionFilters from "./CurrencyConversionFilters";
import { formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const CURRENCY_OPTIONS = [{ value: "MYR", label: "MYR (Malaysian Ringgit)" }];
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const CurrencyConversion = () => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 });
  const [rules, setRules] = useState([]);
  const [filters, setFilters] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    update: false,
  });
  const [editingRule, setEditingRule] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchRules = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));
    try {
      const params = {
        ...(filters.currency && { currency: filters.currency }),
        ...(filters.isActive !== undefined && { isActive: filters.isActive }),
        ...(showArchived && { archived: true }),
        limit: pagination.limit,
        page: pagination.currentPage,
      };
      const res = await axios.get("/api/currency-conversion-rules", { params });
      setRules(res.data.data);
      setPagination((prev) => ({
        ...prev,
        totalSize: res.data.pagination?.totalSize,
      }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, index: false }));
    }
  }, [pagination.currentPage, pagination.limit, filters, showArchived]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const createHandler = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      await axios.post("/api/currency-conversion-rules", {
        currency: values.currency,
        pointsPerUnit: values.pointsPerUnit,
        isActive: values.isActive ?? true,
      });
      message.success("Conversion rule created");
      form.resetFields();
      fetchRules();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const updateHandler = async (ruleId, values) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      await axios.patch(`/api/currency-conversion-rules/${ruleId}`, values);
      message.success("Conversion rule updated");
      setEditingRule(null);
      fetchRules();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const toggleStatusHandler = async (ruleId, isActive) => {
    try {
      await axios.patch(
        `/api/currency-conversion-rules/${ruleId}/toggle-status`,
        { isActive }
      );
      message.success("Status updated");
      fetchRules();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const archiveHandler = (ruleId) => {
    Modal.confirm({
      title: "Archive conversion rule",
      content:
        "Are you sure you want to archive this rule? Base earning will stop for this currency until a new active rule exists.",
      async onOk() {
        try {
          await axios.patch(
            `/api/currency-conversion-rules/${ruleId}/archive`
          );
          message.success("Rule archived");
          fetchRules();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const unarchiveHandler = (ruleId) => {
    Modal.confirm({
      title: "Restore conversion rule",
      content:
        "The rule will be restored as inactive (only one active rule per currency is allowed). Re-enable it with the Active toggle.",
      async onOk() {
        try {
          await axios.patch(
            `/api/currency-conversion-rules/${ruleId}/unarchive`
          );
          message.success("Rule restored");
          fetchRules();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const handleArchivedToggle = (e) => {
    setShowArchived(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (record) => {
    setEditingRule(record);
    editForm.setFieldsValue({
      currency: record.currency,
      pointsPerUnit: record.pointsPerUnit,
      isActive: record.isActive,
    });
  };

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  const columns = [
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 140,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: "Points per 1 unit",
      dataIndex: "pointsPerUnit",
      key: "pointsPerUnit",
      width: 160,
      render: (v) => <span>{v}</span>,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (value, record) =>
        !showArchived &&
        permissions?.includes(PERMISSIONS.TOGGLE_CURRENCY_CONVERSION_STATUS) ? (
          <Switch
            checked={value}
            onChange={(checked) =>
              toggleStatusHandler(record.objectId, checked)
            }
          />
        ) : value ? (
          "Yes"
        ) : (
          "No"
        ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => (text ? formatDateTime(text) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) =>
        showArchived ? (
          permissions?.includes(PERMISSIONS.DELETE_CURRENCY_CONVERSION) && (
            <Button type="link" onClick={() => unarchiveHandler(record.objectId)}>
              Restore
            </Button>
          )
        ) : (
          <>
            {permissions?.includes(PERMISSIONS.UPDATE_CURRENCY_CONVERSION) && (
              <Button type="link" onClick={() => handleEdit(record)}>
                Edit
              </Button>
            )}
            {permissions?.includes(PERMISSIONS.DELETE_CURRENCY_CONVERSION) && (
              <Button
                type="link"
                danger
                onClick={() => archiveHandler(record.objectId)}
              >
                Archive
              </Button>
            )}
          </>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Currency Conversion (Reward base earning)"
        childRoutes={[
          { path: "/currency-conversion", breadcrumbName: "Currency Conversion" },
        ]}
      />

      {permissions?.includes(PERMISSIONS.CREATE_CURRENCY_CONVERSION) && (
        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          style={{ marginBottom: 16 }}
        >
          <Collapse.Panel header="Create new conversion rule" key="create">
            <p style={{ color: "#888" }}>
              Sets how many reward points a partner earns per 1 unit of the
              given currency on each completed order. Only one active rule per
              currency at a time — deactivate the existing rule first if you
              want to create a new one for the same currency.
            </p>
            <Form
              form={form}
              layout="vertical"
              onFinish={createHandler}
              initialValues={{ currency: "MYR", pointsPerUnit: 1, isActive: true }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="currency"
                    label="Currency"
                    rules={[{ required: true, message: "Currency is required" }]}
                  >
                    <Select placeholder="Select currency">
                      {CURRENCY_OPTIONS.map((c) => (
                        <Select.Option key={c.value} value={c.value}>
                          {c.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="pointsPerUnit"
                    label="Points per 1 unit"
                    rules={[
                      {
                        required: true,
                        message: "Points per unit is required",
                      },
                      {
                        type: "number",
                        min: 0.0001,
                        message: "Must be a positive number",
                      },
                    ]}
                  >
                    <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="isActive"
                    label="Active"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading.create}
              >
                Create rule
              </Button>
            </Form>
          </Collapse.Panel>
        </Collapse>
      )}

      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <CurrencyConversionFilters
            filters={filters}
            onFiltersChange={(f) => setFilters(f)}
          />
        </Collapse.Panel>
      </Collapse>

      <Row justify="start" style={{ marginTop: 16, marginBottom: 8 }}>
        <Col>
          <Radio.Group
            value={showArchived}
            onChange={handleArchivedToggle}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value={false}>Active</Radio.Button>
            <Radio.Button value={true}>Archived</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={rules}
        pagination={false}
        loading={loading.index}
        scroll={{ x: true }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>

      <Modal
        title="Edit conversion rule"
        visible={!!editingRule}
        onCancel={() => setEditingRule(null)}
        onOk={() => {
          editForm.validateFields().then((values) => {
            updateHandler(editingRule.objectId, values);
          });
        }}
        confirmLoading={loading.update}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="currency"
            label="Currency"
            rules={[{ required: true, message: "Currency is required" }]}
          >
            <Select>
              {CURRENCY_OPTIONS.map((c) => (
                <Select.Option key={c.value} value={c.value}>
                  {c.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="pointsPerUnit"
            label="Points per 1 unit"
            rules={[
              { required: true, message: "Points per unit is required" },
              { type: "number", min: 0.0001, message: "Must be positive" },
            ]}
          >
            <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CurrencyConversion;
